import { z } from "zod";

import { AccountPlusOutline } from "@/assets/icons/24/AccountPlusOutline";
import { Card } from "@/assets/icons/24/Card";
import Cart from "@/assets/icons/24/Cart";
import { GiftOutline } from "@/assets/icons/24/GiftOutline";
import { HomeOutline } from "@/assets/icons/24/HomeOutline";
import { ProfileOutlined } from "@/assets/icons/24/ProfileOutlined";
import { Search as SearchIcon } from "@/assets/icons/24/Search";
import { StarOutline } from "@/assets/icons/24/StarOutline";
import { TagOutlined } from "@/assets/icons/24/TagOutlined";
import { UmbrellaBeachOutlineIcon } from "@/assets/icons/24/UmbrellaBeachOutline";
import HeartOutlineIcon from "@/assets/icons/24/line/Heart";
import { IconNavigationLocationPinOutline } from "@/assets/icons/figma/IconNavigationLocationPinOutline";
import { IconProfilePersonalProfileOutline } from "@/assets/icons/figma/IconProfilePersonalProfileOutline";
import { IconStateLockedOutline } from "@/assets/icons/figma/IconStateLockedOutline";
import { IconStateNotificationOutline } from "@/assets/icons/figma/IconStateNotificationOutline";
import { IconThemeBriefcaseOutlined } from "@/assets/icons/figma/IconThemeBriefcaseOutlined";
import { IconThemeMoney } from "@/assets/icons/figma/IconThemeMoney";
import {
  AccountMenuGroup,
  AccountMenuItem,
  AccountSettingsEntry,
} from "@/common/contracts/account.contracts";
import { MeQueryMerchant } from "@/common/contracts/member.contracts";
import {
  PASSWORD_HAS_DIGITS_REGEX,
  PASSWORD_HAS_LOWERCASE_CHARS_REGEX,
  PASSWORD_HAS_UPPERCASE_CHARS_REGEX,
} from "@/common/static/regex.static";
import {
  ACCOUNT_ADS_ROUTE,
  ACCOUNT_DASHBOARD_ROUTE,
  ACCOUNT_FAVORITES_ROUTE,
  ACCOUNT_NOTIFICATIONS_ROUTE,
  ACCOUNT_PROMOTIONS_ROUTE,
  ACCOUNT_PURCHASES_ROUTE,
  ACCOUNT_REFER_FRIEND_ROUTE,
  ACCOUNT_REVIEWS_ROUTE,
  ACCOUNT_SALES_ROUTE,
  ACCOUNT_SEARCHES_ROUTE,
  ACCOUNT_SETTINGS_ROUTE,
  ACCOUNT_VACATION_MODE_ROUTE,
  CHAT_ROUTE,
} from "@/common/static/routes.static";
import { AccountSettingsBusinessSettings } from "@/components/Account/AccountSettingsBusinessSettings";
import { AccountSettingsPaymentInformation } from "@/components/Account/AccountSettingsPaymentInformation";
import { AccountSettingsSecuritySettings } from "@/components/Account/AccountSettingsSecuritySettings";
import { IconFolderOutlined } from "@/features/pro-buyer-projects/components/IconFolderOutlined";
import {
  ACCOUNT_PRO_PROJECTS_ROUTE,
  PRO_PROJECTS_COPY,
  PRO_PROJECTS_NAMESPACE,
} from "@/features/pro-buyer-projects/static/pro-buyer-projects.static";
import { MerchantType } from "@whoppah/apollo/schema";
import ContactIcon from "@whoppah/icons/24/Contact";

/**
 * WHO-5453. The account sidebar is grouped. Every group renders a small
 * uppercase title; the order below is the order on the page (Account on top).
 * The sidebar is identical for private and business accounts. Log out and
 * delete account are not items here: they live in the fixed footer of the menu
 * (see AccountLayoutMenuList).
 */
/**
 * WHO-5531. Ungrouped items rendered ABOVE the groups, same pill styling, no
 * group title. Evelien's decision: the Dashboard is both the /account landing
 * page and the first menu item.
 */
export const ACCOUNT_MENU_TOP_ITEMS: AccountMenuItem[] = [
  {
    icon: HomeOutline,
    label: "account-dashboard:dashboard-menu-item",
    labelFallback: "Dashboard",
    path: ACCOUNT_DASHBOARD_ROUTE,
  },
];

export const ACCOUNT_MENU_GROUPS: AccountMenuGroup[] = [
  {
    id: "account",
    title: "account:account-menu-group-account",
    titleFallback: "Account",
    items: [
      {
        icon: ProfileOutlined,
        label: "account:my-account-settings-title",
        path: ACCOUNT_SETTINGS_ROUTE,
      },
      {
        icon: IconThemeMoney,
        label: "account:your-account-settings-payment-information-title",
        path: `${ACCOUNT_SETTINGS_ROUTE}/payment`,
      },
      {
        icon: IconStateLockedOutline,
        label: "account:your-account-settings-security-settings-title",
        path: `${ACCOUNT_SETTINGS_ROUTE}/security`,
      },
      {
        icon: IconStateNotificationOutline,
        label: "account:account-notification-preferences-title",
        labelFallback: "Notifications",
        path: ACCOUNT_NOTIFICATIONS_ROUTE,
        notificationPreferencesOnly: true,
      },
    ],
  },
  {
    id: "selling",
    title: "account:account-menu-group-selling",
    titleFallback: "Selling",
    items: [
      {
        icon: TagOutlined,
        label: "account:my-account-ads-title",
        path: ACCOUNT_ADS_ROUTE,
      },
      {
        icon: Card,
        label: "account:my-account-sales-title",
        path: ACCOUNT_SALES_ROUTE,
      },
      {
        icon: UmbrellaBeachOutlineIcon,
        label: "common:vacation-mode",
        path: ACCOUNT_VACATION_MODE_ROUTE,
      },
      {
        icon: StarOutline,
        label: "common:reviews",
        path: ACCOUNT_REVIEWS_ROUTE,
      },
    ],
  },
  {
    id: "buying",
    title: "account:account-menu-group-buying",
    titleFallback: "Buying",
    items: [
      {
        icon: Cart,
        label: "account:my-account-purchases-title",
        path: ACCOUNT_PURCHASES_ROUTE,
      },
      {
        icon: IconFolderOutlined,
        label: `${PRO_PROJECTS_NAMESPACE}:${PRO_PROJECTS_COPY.menuItem.key}`,
        labelFallback: PRO_PROJECTS_COPY.menuItem.en,
        path: ACCOUNT_PRO_PROJECTS_ROUTE,
        proBuyerProjectsOnly: true,
      },
      {
        icon: HeartOutlineIcon,
        label: "account:my-account-favorites-title-v2",
        path: ACCOUNT_FAVORITES_ROUTE,
      },
      {
        icon: SearchIcon,
        label: "account:my-account-searches-title-v2",
        path: ACCOUNT_SEARCHES_ROUTE,
      },
    ],
  },
  {
    id: "messages",
    title: "account:account-menu-group-messages",
    titleFallback: "Messages",
    items: [
      {
        icon: ContactIcon,
        label: "account:my-account-chats-title",
        path: CHAT_ROUTE,
      },
    ],
  },
  {
    id: "more",
    title: "account:account-menu-group-more",
    titleFallback: "More",
    items: [
      {
        icon: AccountPlusOutline,
        label: "account:my-account-tell-a-friend-title",
        path: ACCOUNT_REFER_FRIEND_ROUTE,
      },
      {
        icon: GiftOutline,
        label: "account:my-account-promo-codes-title",
        path: ACCOUNT_PROMOTIONS_ROUTE,
      },
    ],
  },
];

/** Flat view of the whole menu, for breadcrumbs and anything path-keyed. */
export const ACCOUNT_LAYOUT_ITEMS: AccountMenuItem[] = [
  ...ACCOUNT_MENU_TOP_ITEMS,
  ...ACCOUNT_MENU_GROUPS.flatMap(group => group.items),
];

export const ACCOUNT_OVERVIEW_MAP: Record<string, AccountSettingsEntry> = {
  personal: {
    icon: IconProfilePersonalProfileOutline,
    title: "account:your-account-settings-personal-information-title",
    url: `${ACCOUNT_SETTINGS_ROUTE}/personal`,
    merchantType: [MerchantType.Business, MerchantType.Individual],
    // WHO-5453: no component. Personal details are a section of the flat
    // settings page; the delegator renders that page for this path.
    visible: (_?: MeQueryMerchant) => true,
  },
  business: {
    icon: IconThemeBriefcaseOutlined,
    title: "account:my-account-business-settings-title",
    url: `${ACCOUNT_SETTINGS_ROUTE}/business`,
    // WHO-5386. This used to be `[MerchantType.Business]` with a matching
    // `visible`, so the page was shown only to accounts that were ALREADY a
    // business. The upgrade itself has always lived here, which meant the one
    // page that could serve a private seller wanting to go business was
    // invisible to exactly them, and every such request became a CS handoff
    // where a human made the change by hand.
    //
    // An individual now sees it too, and the component renders in upgrade mode:
    // it states the fee change and requires an explicit confirmation before it
    // will convert anything. See AccountSettingsBusinessSettings.
    merchantType: [MerchantType.Business, MerchantType.Individual],
    component: AccountSettingsBusinessSettings,
    visible: (_?: MeQueryMerchant) => true,
  },
  address: {
    icon: IconNavigationLocationPinOutline,
    title: "account:my-account-address-information-title",
    url: `${ACCOUNT_SETTINGS_ROUTE}/address`,
    merchantType: [MerchantType.Business, MerchantType.Individual],
    // WHO-5453: no component, see `personal`.
    visible: (_?: MeQueryMerchant) => true,
  },
  payment: {
    icon: IconThemeMoney,
    title: "account:your-account-settings-payment-information-title",
    url: `${ACCOUNT_SETTINGS_ROUTE}/payment`,
    merchantType: [MerchantType.Business, MerchantType.Individual],
    component: AccountSettingsPaymentInformation,
    visible: (_?: MeQueryMerchant) => true,
  },
  security: {
    icon: IconStateLockedOutline,
    title: "account:your-account-settings-security-settings-title",
    url: `${ACCOUNT_SETTINGS_ROUTE}/security`,
    merchantType: [MerchantType.Business, MerchantType.Individual],
    component: AccountSettingsSecuritySettings,
    visible: (_?: MeQueryMerchant) => true,
  },
};

export const AccountSettingsSecuritySettingsSchema = z
  .object({
    currentPassword: z
      .string()
      .min(1, { message: "account:your-account-settings-validation-required" }),
    newPassword1: z.string().superRefine((value, ctx) => {
      if (value.length < 8) {
        ctx.addIssue({
          code: z.ZodIssueCode.too_small,
          minimum: 8,
          inclusive: true,
          type: "string",
          path: ["passwordMinLength"],
        });
      }

      if (PASSWORD_HAS_DIGITS_REGEX.exec(value) === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.invalid_string,
          validation: "regex",
          path: ["passwordHasDigits"],
        });
      }

      if (PASSWORD_HAS_LOWERCASE_CHARS_REGEX.exec(value) === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.invalid_string,
          validation: "regex",
          path: ["passwordLowercase"],
        });
      }

      if (PASSWORD_HAS_UPPERCASE_CHARS_REGEX.exec(value) === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.invalid_string,
          validation: "regex",
          path: ["passwordUppercase"],
        });
      }
    }),
    newPassword2: z
      .string()
      .min(1, { message: "account:your-account-settings-validation-required" }),
  })
  .superRefine(({ newPassword1, newPassword2 }, ctx) => {
    if (newPassword1 !== newPassword2) {
      ctx.addIssue({
        code: "custom",
        message: "account:your-account-settings-validation-password-mismatch",
        path: ["newPassword2"],
      });
    }
  });

export type AccountSettingsSecuritySettingsSchemaType = z.infer<
  typeof AccountSettingsSecuritySettingsSchema
>;
